// What a reader is told when an action fails, in the browser: the other half of
// engine/user_errors.py, shared by the root site and the corpus app.
//
// `.claude/rules/user-errors.md`: every failure a reader meets says what failed,
// whose problem it is, and whether trying again helps. Pages never show an
// exception's own text; they call `describeFailure`.
//
// A `UserFacingError` carries a sentence written for the reader -- the server's
// `detail`, which engine/user_errors.py guarantees is one, or a sentence chosen
// here from the status when the server sent none. Anything else (a dropped
// connection, a bug in the page) is described here and logged to the console,
// never shown in its own words: "Failed to fetch" tells a reader nothing.

/** An error whose message is a sentence for the reader. The clients' errors extend it. */
export class UserFacingError extends Error {
  constructor(message) {
    super(message)
    this.name = 'UserFacingError'
  }
}

/**
 * The reader's sentence for a failed response: the server's own when it sent
 * one, else one chosen by status. A FastAPI validation error sends `detail` as
 * a list, which would print as "[object Object]", so only a string counts.
 */
export function responseSentence(detail, status) {
  if (typeof detail === 'string' && detail.trim() !== '') return detail
  if (status === 401) return 'You are signed out. Sign in and try again.'
  if (status === 429) return 'That was too many at once. Wait a moment and try again.'
  if (status >= 500) return 'SourceText could not do that just now; the fault is ours. Try again in a moment.'
  return 'SourceText did not accept that request. Reload the page and try again.'
}

/** The sentence to show for `error`, given what failed in the page's own words. */
export function describeFailure(error, whatFailed) {
  if (error instanceof UserFacingError) return error.message
  console.error(whatFailed, error)
  return `${whatFailed}. SourceText could not be reached; check your connection and try again.`
}
